import React, { createContext, useContext, useEffect, useState } from "react";
import * as defaults from "../mock/mock";
import { getContent } from "../lib/api";

const ContentContext = createContext(null);

export const ContentProvider = ({ children }) => {
  const [override, setOverride] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Skip fetch if no backend configured (e.g. static Vercel deploy)
    const hasBackend = !!process.env.REACT_APP_BACKEND_URL;
    if (!hasBackend) {
      setLoaded(true);
      return;
    }
    getContent()
      .then((c) => setOverride(c || {}))
      .catch(() => setOverride({}))
      .finally(() => setLoaded(true));
  }, []);

  // Merge defaults (mock.js) with backend override
  const player = {
    ...defaults.player,
    ...(override.bio !== undefined && { bio: override.bio }),
    ...(override.tagline !== undefined && { tagline: override.tagline }),
    ...(override.heightCurrent !== undefined && { heightCurrent: override.heightCurrent }),
    ...(override.heightProjected !== undefined && { heightProjected: override.heightProjected }),
    ...(override.weight !== undefined && { weight: override.weight }),
    ...(override.gpa !== undefined && { gpa: override.gpa }),
    ...(override.classOf !== undefined && { classOf: override.classOf }),
    ...(override.school !== undefined && { school: override.school }),
    ...(override.jerseyNumber !== undefined && { jerseyNumber: override.jerseyNumber }),
    ...(override.contactEmail !== undefined && { contactEmail: override.contactEmail }),
    ...(override.recruitingEmail !== undefined && { recruitingEmail: override.recruitingEmail }),
    ...(override.coachName !== undefined && { coachName: override.coachName }),
    ...(override.coachRole !== undefined && { coachRole: override.coachRole }),
    ...(override.coachEmail !== undefined && { coachEmail: override.coachEmail }),
  };

  const seasonStats = {
    season: override.seasonLabel || defaults.seasonStats.season,
    items: [
      { label: "Sacks", value: override.sacks ?? defaults.seasonStats.items[0].value },
      { label: "Tackles", value: override.tackles ?? defaults.seasonStats.items[1].value },
      { label: "Forced Fumbles", value: override.forcedFumbles ?? defaults.seasonStats.items[2].value },
      { label: "Fumble Recoveries", value: override.fumbleRecoveries ?? defaults.seasonStats.items[3].value },
    ],
  };

  const measurables = [
    { label: "Height (Current)", value: player.heightCurrent },
    { label: "Height (Projected)", value: player.heightProjected },
    { label: "Weight", value: player.weight },
    { label: "Class", value: player.classOf },
    { label: "Positions", value: "DE / DT / OT" },
    { label: "GPA", value: player.gpa },
  ];

  const accolades = override.accolades && Array.isArray(override.accolades) && override.accolades.length
    ? override.accolades
    : defaults.accolades;

  const gallery = override.gallery && Array.isArray(override.gallery) && override.gallery.length
    ? override.gallery.map((g, i) => ({ id: i + 1, ...g }))
    : defaults.gallery;

  const highlightVideo = {
    title: override.highlightTitle || defaults.highlightVideo.title,
    youtubeId: override.highlightYoutubeId || defaults.highlightVideo.youtubeId,
    url: `https://youtu.be/${override.highlightYoutubeId || defaults.highlightVideo.youtubeId}`,
    duration: defaults.highlightVideo.duration,
  };

  const campVideo = defaults.campVideo;
  const socialLinks = defaults.socialLinks;

  const academics = {
    gpa: player.gpa,
    school: player.school,
    graduation: `Class of ${player.classOf}`,
    focus: defaults.academics.focus,
  };

  const value = { player, seasonStats, measurables, accolades, gallery, highlightVideo, campVideo, socialLinks, academics, navLinks: defaults.navLinks, loaded };
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used inside ContentProvider");
  return ctx;
};
